import React, { useEffect, useState } from 'react';
import { Check, Clock, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Progress } from '../ui/progress';
import { useI18n } from '../../i18n';
import { overallProgress } from './project-uploads';
import type { UploadEntry } from './useProjectFileUploads';

interface ProjectUploadDialogProps {
  open: boolean;
  entries: UploadEntry[];
}

const StatusIcon: React.FC<{ status: UploadEntry['status'] }> = ({ status }) => {
  switch (status) {
    case 'uploaded':
      return <Check className="h-4 w-4 shrink-0 text-green-600" aria-hidden />;
    case 'failed':
      return <X className="h-4 w-4 shrink-0 text-destructive" aria-hidden />;
    case 'uploading':
      return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />;
    default:
      return <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />;
  }
};

/**
 * Progress of a batch of project uploads: the batch as a whole, then each file.
 * Closing it (X, Escape) only hides it: the batch carries on and its summary
 * toast still reports the result.
 */
export const ProjectUploadDialog: React.FC<ProjectUploadDialogProps> = ({ open, entries }) => {
  const { t } = useI18n();
  const d = t.projectUploads.dialog;
  const [hidden, setHidden] = useState(false);
  // Once the batch ends, the next one opens the dialog again
  useEffect(() => {
    if (!open) setHidden(false);
  }, [open]);
  const finished = entries.filter((e) => e.status === 'uploaded' || e.status === 'failed').length;
  const statusLabel = (e: UploadEntry) =>
    e.status === 'uploading'
      ? `${e.progress}%`
      : e.status === 'uploaded'
        ? d.uploaded
        : e.status === 'failed'
          ? d.failed
          : d.waiting;

  return (
    <Dialog open={open && !hidden} onOpenChange={(isOpen) => setHidden(!isOpen)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{d.title(entries.length)}</DialogTitle>
          <DialogDescription>{d.closeHint}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Progress
            // A failed file is finished too, or the bar would stop short of the end
            value={overallProgress(
              entries.map((e) => (e.status === 'failed' ? { ...e, progress: 100 } : e)),
            )}
            className="w-full"
          />
          <p className="text-center text-sm text-muted-foreground" aria-live="polite">
            {d.progress(finished, entries.length)}
          </p>
          {entries.length > 1 && (
            <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {entries.map((e) => (
                <li key={e.key} className="flex items-center gap-2 text-sm">
                  <StatusIcon status={e.status} />
                  <span className="min-w-0 flex-1 truncate" title={e.error ? `${e.name}: ${e.error}` : e.name}>
                    {e.name}
                  </span>
                  <span
                    className={
                      e.status === 'failed'
                        ? 'shrink-0 text-xs text-destructive'
                        : 'shrink-0 text-xs tabular-nums text-muted-foreground'
                    }
                  >
                    {statusLabel(e)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectUploadDialog;
